const getDirectionsLeg = require("./directionsleg");
const geometry = require("../geometry");
const utils = require("../utils");

/**
 * @typedef {Object} BaiduDirectionsRouteParams
 * @property [summary] {String} Route summary
 * @property [duration] {Number} Route duration in seconds
 */

/**
 * Google DirectionsRoute-like implementation to work with Baidu
 * @private
 */
class BaiduDirectionsRoute {
    /**
     * constructor
     * @param {BMap.Route} route
     * @param {BaiduDirectionsRouteParams} params Route params
     */
    constructor(route, params = {}) {
        /**
         * Map to add route onto
         * @type {null|BMap.Map}
         */
        this.map = null;

        /**
         * The original baidu route.
         * @type {BMap.Route}
         */
        this.route = route;

        /**
         * Bounds of the route
         * @type {woosmap.maps.geometry.LatLngBounds}
         */
        this.bounds = null;

        /**
         * Array of BaiduDirectionsLegs.
         * @type {Array<BaiduDirectionsLeg>}
         */
        this.legs = null;

        /**
         * Array of BMap.Points representing the route.
         * @type {Array<BMap.Point>}
         */
        this.path = null;

        /**
         * Route summary
         * @type {String}
         */
        this.summary = Object.hasOwn(params, "summary") ? params.summary : null;

        /**
         * Route duration in seconds
         * @type {Number}
         * @private
         */
        this._duration = Object.hasOwn(params, "duration") ? Number.parseInt(params.duration) : null;

        // not used
        this.coyprights = null;
        this.fare = null;
        this.warnings = null;
        this.waypoint_order = null;
        this.overview_path = null;
        this.overview_polyline = null;

        // init
        this._setPath();
        this._setBounds();
        this._setLegs();
    }

    /**
     * Initialize the path.
     * @private
     */
    _setPath() {
        this.path = this.route.getPath();
        this.overview_path = this.path;
    }

    /**
     * Initialize the bounds based on path
     * @private
     */
    _setBounds() {
        const bounds = new geometry.LatLngBounds(null, null);
        const path = Object.assign([], this.path);
        path.sort((a, b) => a.lat - b.lat)
            .sort((a, b) => a.lng - b.lng)
            .map((item) => {
                const point = new geometry.LatLng(item.lat, item.lng);
                bounds.extend(point);
            });

        this.bounds = bounds;
    }

    /**
     * Initialize legs with an array of one BaiduDirectionsLeg. With google there is a leg for each part of a destination with a waypoint
     * or for multiple destinations.
     * We don't have waypoint at this time, nor handle multiple destinations, so only one leg is present.
     * @private
     */
    _setLegs() {
        const steps = [];
        for (let it = 0, ln = this.route.getNumSteps(); it < ln; it++) {
            steps.push(this.route.getStep(it));
        }

        this.legs = [getDirectionsLeg(steps, this._duration)];
    }

    /**
     * Returns the bounds.
     * @returns {woosmap.maps.geometry.LatLngBounds}
     */
    getBounds() {
        return this.bounds;
    }

    /**
     * Returns Baidu path (usefull for BMap.Map.setViewport())
     * @returns {Array<BMap.Point>}
     */
    getPath() {
        return this.path;
    }

    /**
     * Show the route on the map
     * @param map {null|BMap.Map} map
     */
    setMap(map = null) {
        this.map = map;
        this.polyline.setMap(this.map);
    }

    /**
     * Returns the map where the polyline is shown
     * @param map
     * @returns {null|BMap.Map}
     */
    getMap(map) {
        return this.map;
    }
}

/**
 * Gets route abstraction.
 * @private
 * @param {BMap.Route|google.maps.DirectionsRoute} route route given by the current loaded library (Google/Baidu)
 * @param {BaiduDirectionsRouteParams} params route params
 * @returns {BaiduDirectionsRoute|google.maps.DirectionsRoute}
 */
function getDirectionsRoute(route, params = {}) {
    if (utils.isBaidu()) {
        return new BaiduDirectionsRoute(route, params);
    }
    return route;
}

module.exports = getDirectionsRoute;
